import { useEffect, useState } from 'react';
import { userAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  PageHeader, Button, Table, StatusBadge, Modal,
  Input, Select, Loading, Alert, Badge
} from '../components/ui';

const ROLES = ['admin', 'transport_officer', 'manager', 'mechanic', 'driver', 'foreman', 'accountant'];

const roleColors = {
  admin:             'purple',
  transport_officer: 'blue',
  manager:           'green',
  mechanic:          'amber',
  driver:            'gray',
};

const emptyForm = {
  name: '', email: '', password: '', role: 'driver', phone: '',
  designation: '', department: '', employee_no: '', nic: '',
};

// Extra profile fields shared by the add + edit modals
const ProfileFields = ({ form, setForm }) => (
  <div className="grid grid-cols-2 gap-4">
    <Input label="Designation" value={form.designation || ''}
      onChange={e => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Transport Officer" />
    <Input label="Department" value={form.department || ''}
      onChange={e => setForm({ ...form, department: e.target.value })} placeholder="e.g. Administration" />
    <Input label="Employee No." value={form.employee_no || ''}
      onChange={e => setForm({ ...form, employee_no: e.target.value })} />
    <Input label="NIC" value={form.nic || ''}
      onChange={e => setForm({ ...form, nic: e.target.value })} placeholder="e.g. 199012345678" />
  </div>);

const EXIT_REASONS = [
  ['resigned','Resigned'], ['retired','Retired / Pension'], ['on_leave','On Leave'],
  ['transferred','Transferred'], ['terminated','Terminated'], ['suspended','Suspended'], ['other','Other'],
];

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [search,  setSearch]  = useState('');
  const [filterRole, setFilterRole] = useState('');

  // Modals
  const [addModal,  setAddModal]  = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [deact,     setDeact]     = useState(null);
  const [deactForm, setDeactForm] = useState({ reason:'resigned', date:'', note:'' });
  const [saving,    setSaving]    = useState(false);
  const [showPass,  setShowPass]  = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────
  const load = () => {
    setLoading(true);
    userAPI.getAll()
      .then(r => setUsers(r.data.data))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const flash = (msg, isError = false) => {
    isError ? setError(msg) : setSuccess(msg);
    setTimeout(() => isError ? setError('') : setSuccess(''), 3500);
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    const matchRole   = !filterRole || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const roleCounts = ROLES.reduce((acc, r) => {
    acc[r] = users.filter(u => u.role === r).length;
    return acc;
  }, {});

  // ── Create user ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm(emptyForm);
    setShowPass(false);
    setAddModal(true);
  };

  const saveNew = async e => {
    e.preventDefault();
    if (!form.password || form.password.length < 6) {
      return flash('Password must be at least 6 characters', true);
    }
    setSaving(true);
    try {
      await authAPI.register(form);
      setAddModal(false);
      load();
      flash(`User "${form.name}" created successfully`);
    } catch (err) {
      flash(err.response?.data?.message || 'Failed to create user', true);
    } finally { setSaving(false); }
  };

  // ── Edit user ────────────────────────────────────────────────────────────────
  const openEdit = u => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, phone: u.phone || '',
      designation: u.designation || '', department: u.department || '',
      employee_no: u.employee_no || '', nic: u.nic || '' });
    setEditModal(true);
  };

  const saveEdit = async e => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.name, phone: form.phone, role: form.role };
    if (form.password) payload.password = form.password;
    try {
      await userAPI.update(editing.id, payload);
      setEditModal(false);
      load();
      flash(`User "${form.name}" updated`);
    } catch (err) {
      flash(err.response?.data?.message || 'Failed to update', true);
    } finally { setSaving(false); }
  };

  // ── Toggle active ────────────────────────────────────────────────────────────
  const toggle = async u => {
    if (u.id === currentUser.id) return flash('You cannot deactivate your own account', true);
    if (u.is_active) {
      // Deactivating asks why (resigned / pension / leave …)
      setDeact(u);
      setDeactForm({ reason: 'resigned', date: new Date().toISOString().split('T')[0], note: '' });
      return;
    }
    if (!window.confirm(`Reactivate "${u.name}"?`)) return;
    try { await userAPI.toggle(u.id); load(); flash(`User "${u.name}" reactivated`); }
    catch { flash('Failed to update user status', true); }
  };

  const submitDeact = async (e) => {
    e.preventDefault();
    try {
      await userAPI.toggle(deact.id, deactForm);
      const label = EXIT_REASONS.find(([k]) => k === deactForm.reason)?.[1] || deactForm.reason;
      flash(`"${deact.name}" deactivated — ${label}`);
      setDeact(null); load();
    } catch (err) {
      flash(err.response?.data?.message || 'Failed to update user status', true);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="User Management"
        sub={`${users.length} total users`}
        action={
          <Button onClick={openAdd}>
            + Add User
          </Button>
        }
      />

      {/* Role summary pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        {ROLES.map(r => (
          <button key={r}
            onClick={() => setFilterRole(filterRole === r ? '' : r)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
              ${filterRole === r
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'}`}>
            {r.replace('_', ' ')} ({roleCounts[r] || 0})
          </button>
        ))}
        {filterRole && (
          <button onClick={() => setFilterRole('')}
            className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:text-gray-700">
            Clear ×
          </button>
        )}
      </div>

      {/* Search */}
      <div className="mb-5">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg w-72 outline-none focus:ring-2 focus:ring-primary-300" />
      </div>

      {/* Alerts */}
      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      {/* Table */}
      {loading ? <Loading /> : (
        <Table headers={['User', 'Employee No. / NIC', 'Email', 'Role', 'Phone', 'Status', 'Actions']}>
          {filtered.map(u => (
            <tr key={u.id} className={`hover:bg-gray-50 ${!u.is_active ? 'opacity-50' : ''}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center
                                  text-primary-600 text-xs font-bold flex-shrink-0">
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{u.name}
                      {u.id === currentUser.id && (
                        <span className="ml-2 text-xs text-primary-500 font-medium">You</span>
                      )}
                    </p>
                    {(u.designation || u.department) && (
                      <p className="text-xs text-gray-400">
                        {u.designation}{u.designation && u.department ? ' · ' : ''}{u.department}
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {u.employee_no || '—'}
                {u.nic && <p className="text-xs text-gray-400">{u.nic}</p>}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                  ${u.role === 'admin'             ? 'bg-purple-100 text-purple-700'  : ''}
                  ${u.role === 'transport_officer' ? 'bg-blue-100   text-blue-700'    : ''}
                  ${u.role === 'manager'           ? 'bg-green-100  text-green-700'   : ''}
                  ${u.role === 'mechanic'          ? 'bg-amber-100  text-amber-700'   : ''}
                  ${u.role === 'driver'            ? 'bg-gray-100   text-gray-700'    : ''}
                  ${u.role === 'foreman'           ? 'bg-indigo-100 text-indigo-700'  : ''}
                  ${u.role === 'accountant'        ? 'bg-teal-100   text-teal-700'    : ''}`}>
                  {u.role.replace('_', ' ')}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{u.phone || '—'}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                  ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {u.is_active ? 'Active' : 'Inactive'}
                </span>
                {!u.is_active && u.inactive_reason && (
                  <p className="text-xs text-gray-400 mt-1 capitalize" title={u.inactive_note || ''}>
                    {(u.inactive_reason || '').replace('_', ' ')}
                    {u.inactive_date ? ` · ${u.inactive_date}` : ''}
                  </p>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>
                    Edit
                  </Button>
                  {u.id !== currentUser.id && (
                    <Button
                      variant={u.is_active ? 'danger' : 'success'}
                      size="sm"
                      onClick={() => toggle(u)}>
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">
                No users found
              </td>
            </tr>
          )}
        </Table>
      )}

      {/* ── Add User Modal ──────────────────────────────────────────────────── */}
      {/* ══ DEACTIVATE WITH REASON ════════════════════════════════════════════ */}
      <Modal open={!!deact} onClose={() => setDeact(null)} title={`Deactivate — ${deact?.name || ''}`} size="md">
        {deact && (
          <form onSubmit={submitDeact} className="space-y-4">
            <p className="text-sm text-gray-500">
              The account keeps all its history and can be reactivated later.
            </p>
            <Select label="Reason *" value={deactForm.reason}
              onChange={e => setDeactForm({ ...deactForm, reason: e.target.value })}>
              {EXIT_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Select>
            <Input label="Effective Date" type="date" value={deactForm.date}
              onChange={e => setDeactForm({ ...deactForm, date: e.target.value })} />
            <Input label="Note" value={deactForm.note}
              onChange={e => setDeactForm({ ...deactForm, note: e.target.value })}
              placeholder="optional — e.g. returning 01 Sep, handover to K. Silva" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setDeact(null)}>Cancel</Button>
              <Button type="submit" variant="danger">Deactivate</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={addModal} onClose={() => setAddModal(false)} title="Add New User" size="md">
        <form onSubmit={saveNew} className="space-y-4">
          <Input
            label="Full Name *"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            required
            placeholder="e.g. Nimal Perera"
          />
          <Input
            label="Email Address *"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            required
            placeholder="user@hit.lk"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password * <span className="text-gray-400 font-normal">(min 6 characters)</span>
            </label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                required
                placeholder="Enter password"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none
                           focus:ring-2 focus:ring-primary-300 focus:border-primary-400 pr-20"
              />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700">
                {showPass ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <Select
            label="Role *"
            value={form.role}
            onChange={e => setForm({ ...form, role: e.target.value })}
            required>
            {ROLES.map(r => (
              <option key={r} value={r}>{r.replace('_', ' ')}</option>
            ))}
          </Select>
          <Input
            label="Phone Number"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="0771234567"
          />
          <ProfileFields form={form} setForm={setForm} />

          {/* Role info box */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg px-3 py-2.5 text-xs text-primary-700">
            {form.role === 'admin'             && '🔐 Full system access — all modules, delete, user management'}
            {form.role === 'transport_officer' && '🚛 Vehicles, drivers, assignments, fuel, compliance, repairs'}
            {form.role === 'manager'           && '📊 View all data + approve or reject repair requests'}
            {form.role === 'mechanic'          && '🔧 View repairs and mark jobs as completed'}
            {form.role === 'driver'            && '👤 Own assignments + submit repair requests only'}
            {form.role === 'foreman'           && '🖊️ Approves or rejects repair requests and garage invoices'}
            {form.role === 'accountant'        && '💳 Records invoice payments (cheque / cash) in Accounts'}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setAddModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating…' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Edit User Modal ─────────────────────────────────────────────────── */}
      <Modal open={editModal} onClose={() => setEditModal(false)} title={`Edit — ${editing?.name}`} size="md">
        <form onSubmit={saveEdit} className="space-y-4">
          <Input
            label="Full Name *"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            required
          />
          <Input
            label="Email Address"
            type="email"
            value={form.email}
            disabled
            className="bg-gray-50 cursor-not-allowed opacity-60"
          />
          <Select
            label="Role *"
            value={form.role}
            onChange={e => setForm({ ...form, role: e.target.value })}
            required>
            {ROLES.map(r => (
              <option key={r} value={r}>{r.replace('_', ' ')}</option>
            ))}
          </Select>
          <Input
            label="Phone Number"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="0771234567"
          />
          <ProfileFields form={form} setForm={setForm} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password <span className="text-gray-400 font-normal">(leave blank to keep current)</span>
            </label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="Enter new password (optional)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none
                         focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setEditModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
